// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Card, Grid, Stack, Textarea, Title } from '@mantine/core';
import { TimeInput } from '@mantine/dates';
import type { UseFormReturnType } from '@mantine/form';
import type { Coding, Encounter, Patient } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import type { JSX } from 'react';
import { useEffect } from 'react';
import { showErrorNotification } from '../../utils/notifications';
import type { ChartFormValues, LoadedNoteObservations, NoteCode } from './encounter-chart-types';
import { NOTE_CODES } from './encounter-chart-types';

const NOTE_OBSERVATION_SYSTEM = 'https://polarissleep.com/fhir/observation-type';

interface EncounterNotesSectionProps {
  form: UseFormReturnType<ChartFormValues>;
  encounter: Encounter;
  patient: Patient;
  disabled?: boolean;
  onNotesLoaded: (notes: LoadedNoteObservations) => void;
}

export const EncounterNotesSection = ({
  form,
  encounter,
  disabled = false,
  onNotesLoaded,
}: EncounterNotesSectionProps): JSX.Element => {
  const medplum = useMedplum();

  // biome-ignore lint/correctness/useExhaustiveDependencies: onNotesLoaded is a stable callback ref
  useEffect(() => {
    const fetchNotes = async (): Promise<void> => {
      const results = await medplum.searchResources(
        'Observation',
        `encounter=Encounter/${encounter.id}&code=${NOTE_CODES.map((c) => `${NOTE_OBSERVATION_SYSTEM}|${c}`).join(',')}`,
        { cache: 'no-cache' }
      );
      const empty = { id: undefined, text: '' };
      const loaded: LoadedNoteObservations = {
        assessment: { ...empty },
        hpi: { ...empty },
        'treatment-plan': { ...empty },
        'vital-signs': { ...empty },
        'review-symptoms': { ...empty },
        exam: { ...empty },
        'patient-note': { ...empty },
        'referring-note': { ...empty },
      };
      for (const obs of results) {
        const code = obs.code?.coding?.find((c: Coding) => c.system === NOTE_OBSERVATION_SYSTEM)?.code as
          | NoteCode
          | undefined;
        if (code && code in loaded) {
          loaded[code] = { id: obs.id, text: obs.valueString ?? '' };
        }
      }
      onNotesLoaded(loaded);
    };
    fetchNotes().catch(showErrorNotification);
  }, [medplum, encounter.id]);
  return (
    <Card withBorder p={20}>
      <Title order={2} mb={10}>
        Notes
      </Title>
      <Stack gap="md">
        <Grid>
          <Grid.Col span={6}>
            <TimeInput label="Appointment Start Time" disabled={disabled} {...form.getInputProps('startTime')} />
          </Grid.Col>
          <Grid.Col span={6}>
            <TimeInput label="Appointment End Time" disabled={disabled} {...form.getInputProps('endTime')} />
          </Grid.Col>
        </Grid>

        <Textarea
          label="Treatment Plan"
          placeholder="Outline the recommended treatment plan and next steps..."
          disabled={disabled}
          autosize
          minRows={3}
          {...form.getInputProps('treatmentPlan')}
        />

        <Textarea
          label="Vital Signs"
          placeholder="Document relevant vital signs..."
          disabled={disabled}
          autosize
          minRows={3}
          {...form.getInputProps('vitalSigns')}
        />

        <Textarea
          label="Review Symptoms"
          placeholder="Document review of symptoms..."
          disabled={disabled}
          autosize
          minRows={3}
          {...form.getInputProps('reviewSymptoms')}
        />

        <Textarea
          label="Exam"
          placeholder="Document examination findings..."
          disabled={disabled}
          autosize
          minRows={3}
          {...form.getInputProps('exam')}
        />

        <Textarea
          label="Note for Patient"
          placeholder="Patient-facing notes visible in the patient portal..."
          disabled={disabled}
          autosize
          minRows={3}
          {...form.getInputProps('noteForPatient')}
        />

        <Textarea
          label="Note for Referring Provider"
          placeholder="Notes to share with the referring provider..."
          disabled={disabled}
          autosize
          minRows={3}
          {...form.getInputProps('noteForReferring')}
        />
      </Stack>
    </Card>
  );
};
